import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

/**
 * shadcn-style Calendar wrapper around `react-day-picker`. Theme classes use
 * the same design tokens as the rest of the app so range selection, hover,
 * and selected states match the existing button + popover styles.
 *
 * Day decoration: pass `modifiers` like `{ hasMeals: Date[] }` and the
 * matching `modifiersClassNames` for visual markers (the date-range picker
 * uses this to show colored dots on days with logged meals / emotions /
 * exercise).
 */
export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) => (
  <DayPicker
    showOutsideDays={showOutsideDays}
    className={cn('p-3', className)}
    classNames={{
      months: 'flex flex-col sm:flex-row gap-3',
      month: 'space-y-3',
      caption: 'flex justify-center pt-1 relative items-center',
      caption_label: 'text-sm font-medium',
      nav: 'space-x-1 flex items-center',
      nav_button: cn(
        buttonVariants({ variant: 'outline' }),
        'h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100',
      ),
      nav_button_previous: 'absolute left-1',
      nav_button_next: 'absolute right-1',
      table: 'w-full border-collapse space-y-1',
      head_row: 'flex',
      head_cell: 'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
      row: 'flex w-full mt-1',
      // The cell wrapper needs to handle range styling. The selected range
      // start/middle/end apply background classes via the day variants below.
      cell: cn(
        'relative p-0 text-center text-sm',
        'first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md',
        '[&:has([aria-selected].day-range-end)]:rounded-r-md',
        '[&:has([aria-selected].day-outside)]:bg-accent/50',
        '[&:has([aria-selected])]:bg-accent',
        'focus-within:relative focus-within:z-20',
      ),
      day: cn(
        buttonVariants({ variant: 'ghost' }),
        // `relative` is required so day-marker pseudo-elements (the dots
        // beneath days with logged meals/emotions/etc.) anchor to the day
        // button rather than a far-away ancestor — `<td>` doesn't reliably
        // honor `position: relative` across all browsers.
        'relative h-8 w-8 p-0 font-normal aria-selected:opacity-100',
      ),
      day_range_start:
        'day-range-start bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
      day_range_end:
        'day-range-end bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
      day_selected:
        'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
      day_today: 'bg-accent text-accent-foreground',
      day_outside:
        'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
      day_disabled: 'text-muted-foreground opacity-50',
      day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
      day_hidden: 'invisible',
      ...classNames,
    }}
    components={{
      IconLeft: () => <ChevronLeft className="h-4 w-4" />,
      IconRight: () => <ChevronRight className="h-4 w-4" />,
    }}
    {...props}
  />
);
Calendar.displayName = 'Calendar';
